import type { PriceId } from '@iheartjane/types';
import kebabCase from 'lodash-es/kebabCase';
import qs from 'qs';

import type {
  EcommPathsConfig,
  MenuCategoryPath,
  MenuProduct,
  MenuRowPath,
  MenuSpecialPath,
  MenuVirtualBudtenderPath,
  NewProductReviewPath,
  Query,
} from '@jane/shared/types';

import { getLocationQueryWithoutSearchState } from '../locationQueryUtils';
import { buildPath } from './buildPath';

export const getEcommPaths = (
  config: EcommPathsConfig,
  sharedQueryParams?: string
) => {
  const { appMode, storePathProps } = config;

  const useStoreSlug = appMode === 'default';

  let storePath = `stores/${storePathProps?.id}`;
  if (useStoreSlug) {
    storePath += `/${storePathProps?.slug || kebabCase(storePathProps?.name)}`;
  }

  const menuPath =
    appMode === 'default'
      ? `/${storePath}/menu`
      : appMode === 'bloom'
        ? ''
        : '/menu';

  // Maintain any non-search query params when changing pages
  const query = getLocationQueryWithoutSearchState(sharedQueryParams || '');
  const parsedQuery = query ? qs.parse(query) : undefined;

  return {
    cart() {
      return buildPath(config, '/cart', query);
    },
    checkout() {
      return buildPath(config, '/cart/checkout', query);
    },
    login() {
      return buildPath(config, '/login', query);
    },
    menu(menuQuery?: Query) {
      const queryParams = menuQuery
        ? typeof menuQuery === 'object'
          ? { ...menuQuery, ...parsedQuery }
          : `${menuQuery}${query ? `&${query}` : ''}`
        : parsedQuery;
      return buildPath(config, `${menuPath}`, queryParams);
    },
    menuBrandSpecial({ id, title }: MenuSpecialPath) {
      return buildPath(
        config,
        `${menuPath}/jane-gold/${id}/${kebabCase(title)}`,
        query
      );
    },
    menuCategory(category: MenuCategoryPath) {
      return buildPath(config, `${menuPath}/${category}`, query);
    },
    menuFeaturedCategory(featuredCategory: string) {
      return buildPath(
        config,
        `${menuPath}/featured/${encodeURIComponent(
          kebabCase(featuredCategory)
        )}`,
        query
      );
    },
    menuRow({ id, title }: MenuRowPath) {
      return buildPath(
        config,
        `${menuPath}/rows/${id}/${kebabCase(title)}`,
        query
      );
    },
    menuReviews() {
      return buildPath(config, `/${storePath}/reviews`, query);
    },
    menuProduct(
      product: Pick<MenuProduct, 'brand' | 'id' | 'name' | 'url_slug'>,
      defaultWeight?: PriceId
    ) {
      const queryParams = defaultWeight
        ? { ...parsedQuery, defaultWeight }
        : parsedQuery;

      const slug = product.url_slug
        ? product.url_slug
        : kebabCase(`${product.brand ? product.brand : ''} ${product.name}`);
      return buildPath(
        config,
        `${menuPath}/products/${product.id}/${slug}`,
        queryParams
      );
    },
    menuSpecial({ id, title }: MenuSpecialPath) {
      return buildPath(
        config,
        `${menuPath}${appMode === 'bloom' ? '/deals' : '/specials'}/${id}/${kebabCase(title)}`,
        query
      );
    },
    newProductReview({ id, name }: NewProductReviewPath) {
      return buildPath(
        config,
        `/products/${id}/${encodeURIComponent(name)}/reviews/new/`,
        {
          storeId: storePathProps?.id,
          storeName: storePathProps?.name,
          ...parsedQuery,
        }
      );
    },
    profile() {
      return buildPath(config, '/profile', query);
    },
    vb({ displayText, query }: MenuVirtualBudtenderPath) {
      return buildPath(config, `${menuPath}/vb`, {
        t: displayText,
        q: query,
        ...parsedQuery,
      });
    },
  };
};
